import { Popover, Transition } from "@headlessui/react";
import { Fragment } from "react";
import React, { useEffect, useState } from "react";
import { UserCircleIcon, ArrowRightOnRectangleIcon, UserPlusIcon } from "@heroicons/react/24/outline";
import SwitchDarkMode from "shared/SwitchDarkMode/SwitchDarkMode";
import { useUI } from "context/UIContext";
import Cookies from "js-cookie";

export default function AvatarDropdown() {
  const merchantname = Cookies.get("merchantname");
  const userDataString = Cookies.get("userDataaInfo");
  const customerInfo = Cookies.get("CustomerInfo");
  const SignupData = Cookies.get("SignupData");
  const CustomerAdded = Cookies.get("customerAdded");
  const { openAuthModal } = useUI();

  const [userData, setUserData] = useState<any>(null);

  useEffect(() => {
    try {
      if (customerInfo) {
        const parsed = JSON.parse(customerInfo);
        if (parsed && typeof parsed === "object" && parsed.firstName) {
          setUserData(parsed);
          return;
        }
      }
      if (CustomerAdded) {
        const parsed = JSON.parse(CustomerAdded);
        if (parsed && typeof parsed === "object" && parsed.firstName) {
          setUserData(parsed);
          return;
        }
      }
      setUserData(null);
    } catch {
      setUserData(null);
    }
  }, [customerInfo, userDataString, SignupData, CustomerAdded]);

  // Session timeout check
  useEffect(() => {
    const checkSessionTimeout = () => {
      const loginTime = Cookies.get("loginTime");
      if (!loginTime) return;
      const timeElapsed = new Date().getTime() - parseInt(loginTime, 10);
      if (timeElapsed > 24 * 60 * 60 * 1000) {
        handleSignOut();
      }
    };
    const interval = setInterval(checkSessionTimeout, 5 * 60 * 1000); // Check every 5 minutes
    return () => clearInterval(interval);
  }, []);

  const handleSignOut = () => {
    Cookies.remove("CustomerInfo");
    Cookies.remove("userDataaInfo");
    Cookies.remove("addedPayments");
    Cookies.remove("authToken");
    Cookies.remove("BillingAddress");
    Cookies.remove("checkTime");
    Cookies.remove("fromTime");
    Cookies.remove("toTime");
    sessionStorage.removeItem("orderCards");
    Cookies.remove("user");
    Cookies.remove("SignupData");
    Cookies.remove("customerAdded");
    Cookies.remove("loginTime");
    Cookies.remove("orderFee");
    Cookies.remove("paymentId");
    Cookies.remove("orderFeeLabel");
    Cookies.remove("phone");
    sessionStorage.removeItem("onlineOrderSettingsLogo");
    window.location.href = `/${merchantname}`;
  };

  return (
    <div className="AvatarDropdown">
      <Popover className="relative">
        {({ close }) => (
          <>
            <Popover.Button title="Account" aria-label="Account menu" className="w-10 h-10 sm:w-12 sm:h-12 rounded-full text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 focus:outline-none flex items-center justify-center">
              <svg
                className="w-6 h-6"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M20.5899 22C20.5899 18.13 16.7399 15 11.9999 15C7.25991 15 3.40991 18.13 3.40991 22"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Popover.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel className="absolute z-10 w-screen max-w-[240px] px-4 mt-3.5 -right-10 sm:right-0 sm:px-0">
                <div className="overflow-hidden rounded-2xl shadow-lg ring-1 ring-black/5 dark:ring-white/10 bg-white dark:bg-slate-800">
                  {userData === null ? (
                    <div className="py-4 px-4 space-y-1">
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 px-2 pb-2">
                        My Account
                      </p>
                      <button
                        onClick={() => {
                          close();
                          openAuthModal("login");
                        }}
                        className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                      >
                        <ArrowRightOnRectangleIcon className="w-5 h-5 text-primary-500 dark:text-primary-400" />
                        Sign In
                      </button>
                      <button
                        onClick={() => {
                          close();
                          openAuthModal("signup");
                        }}
                        className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                      >
                        <UserPlusIcon className="w-5 h-5 text-primary-500 dark:text-primary-400" />
                        Create Account
                      </button>
                      <div className="border-t border-slate-100 dark:border-slate-700 pt-2 mt-2 flex items-center justify-between px-2">
                        <span className="text-xs text-slate-400 dark:text-slate-500">
                          Theme
                        </span>
                        <SwitchDarkMode />
                      </div>
                    </div>
                  ) : (
                    <div className="py-4 px-4 space-y-1">
                      <div className="px-2 pb-2 border-b border-slate-100 dark:border-slate-700">
                        <p className="text-sm font-bold text-slate-900 dark:text-slate-100">
                          {userData?.firstName} {userData?.lastName}
                        </p>
                        {userData?.email && (
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                            {userData.email}
                          </p>
                        )}
                      </div>
                      <a
                        href={`/${merchantname}/account`}
                        onClick={() => close()}
                        className="flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                      >
                        <UserCircleIcon className="w-5 h-5 text-primary-500 dark:text-primary-400" />
                        Profile
                      </a>
                      <a
                        href={`/${merchantname}/account-my-order`}
                        onClick={() => close()}
                        className="flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                      >
                        <svg className="w-5 h-5 text-primary-500 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Order History
                      </a>
                      <div className="border-t border-slate-100 dark:border-slate-700 pt-2 mt-1">
                        <div className="flex items-center justify-between px-2 mb-2">
                          <span className="text-xs text-slate-400 dark:text-slate-500">
                            Theme
                          </span>
                          <SwitchDarkMode />
                        </div>
                        <button
                          onClick={handleSignOut}
                          className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
                          </svg>
                          Sign Out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    </div>
  );
}
